import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Share2, Copy, Check, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

// Generate numeric ID from UUID (same algorithm as backend/sidebar)
function getShortId(uuid) {
  if (!uuid) return null;
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    hash = ((hash << 5) - hash) + uuid.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash).toString().slice(-6).padStart(4, '0');
}

export default function ShareScheduleCard({ studioId, shortCode }) {
  const [copied, setCopied] = useState(false);

  // Get the customer portal URL from environment or default
  const customerPortalUrl = import.meta.env.VITE_CUSTOMER_PORTAL_URL || 'https://mystudioclass.com';
  // Use shortCode if available, otherwise calculate from studioId
  const identifier = shortCode || getShortId(studioId);
  // New clean URL format: mystudioclass.com/{studioname}
  const publicScheduleUrl = `${customerPortalUrl}/${identifier}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicScheduleUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy link');
    }
  };

  const handleOpenLink = () => {
    window.open(publicScheduleUrl, '_blank');
  };

  if (!studioId && !shortCode) return null;

  return (
    <div
      className="p-6 rounded-2xl transition-all duration-300"
      style={{
        background: 'linear-gradient(145deg, var(--bg-elevated) 0%, var(--bg-surface) 100%)',
        border: '1px solid var(--border-muted)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12), inset 0 1px 0 var(--highlight-10)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top shine effect */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 1,
        background: 'linear-gradient(90deg, transparent, var(--accent-30), var(--highlight-15), var(--accent-30), transparent)',
        pointerEvents: 'none',
      }} />
      <div className="flex items-center gap-2 mb-4">
        <Share2 className="w-5 h-5 text-[var(--text-secondary)]" />
        <h2 className="text-lg md:text-xl font-bold text-[var(--text-primary)]">Share Your Schedule</h2>
      </div>

      <p className="text-sm text-[var(--text-secondary)] mb-4">
        Let anyone view your class schedule. They can sign up when they want to book.
      </p>

      <div className="flex flex-col md:flex-row gap-4 items-center">
        {/* QR Code */}
        <div
          className="p-4 rounded-xl"
          style={{
            background: '#ffffff',
            border: '1px solid var(--border-muted)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 0 20px var(--accent-10)',
          }}
        >
          <QRCodeSVG
            value={publicScheduleUrl}
            size={140}
            level="M"
            includeMargin={true}
            bgColor="#ffffff"
            fgColor="#1a365d"
          />
        </div>

        {/* Link and actions */}
        <div className="flex-1 w-full md:w-auto space-y-3">
          {/* Copyable link */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={publicScheduleUrl}
              readOnly
              className="text-sm flex-1 truncate"
              style={{
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-subtle)',
                padding: '0.625rem 1rem',
                borderRadius: '0.5rem',
                width: '100%'
              }}
            />
            <button
              onClick={handleCopy}
              className={`btn ${copied ? 'btn-primary' : 'btn-outline'} flex-shrink-0 transition-all`}
              title="Copy link"
            >
              {copied ? (
                <Check className="w-4 h-4" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="btn btn-outline flex-1 text-sm py-2"
            >
              <Copy className="w-4 h-4 mr-1" />
              Copy Link
            </button>
            <button
              onClick={handleOpenLink}
              className="btn btn-outline flex-1 text-sm py-2"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Preview
            </button>
          </div>

          <p className="text-xs text-center md:text-left" style={{ color: 'var(--text-muted)' }}>
            Print the QR code or share the link with potential customers
          </p>
        </div>
      </div>
    </div>
  );
}
